import clsx from "clsx";
import {
  BriefcaseBusinessIcon,
  EllipsisIcon,
  MailIcon,
  RotateCcw,
  UserIcon,
  UserRoundX,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSuccessToast } from "@/hooks";
import {
  useCancelUserInvitation,
  useResendUserInvitation,
} from "@/hooks/mutations";
import { useSetRemoveCompanyUserInCompanyUsersForSettings } from "@/hooks/sets";
import { CompanyUser } from "@/types";

export const AccountSettingsCompanyUsersTable = ({
  companyUsers,
  currentUserEmail,
  currentUserId,
}: {
  companyUsers: CompanyUser[];
  currentUserEmail: string;
  currentUserId: number;
}) => {
  const { mutate: cancelUserInvitation } = useCancelUserInvitation();
  const { mutate: resendUserInvitation } = useResendUserInvitation();
  const setRemoveCompanyUserInCompanyUsersForSettings =
    useSetRemoveCompanyUserInCompanyUsersForSettings();
  const successToast = useSuccessToast();

  const currentCompanyUser = companyUsers.find(
    (companyUser) => companyUser.email === currentUserEmail,
  );
  if (!currentCompanyUser) {
    throw new Error("Current user not found");
  }

  // TODO: This is a bit of a hack to get the current user to the top of the list.
  const orderedCompanyUsers = [
    currentCompanyUser,
    ...companyUsers.filter(
      (companyUser) => companyUser.email !== currentUserEmail,
    ),
  ];

  return (
    <div className="flex flex-col overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[25%]">
                <div className="flex items-center gap-1">
                  <UserIcon className="h-4 w-4 text-slate-700" />
                  <p className="font-semibold text-slate-700">Name</p>
                </div>
              </TableHead>
              <TableHead className="w-[33%]">
                <div className="flex items-center gap-1">
                  <MailIcon className="h-4 w-4 text-slate-700" />
                  <p className="font-semibold text-slate-700">Email</p>
                </div>
              </TableHead>
              <TableHead className="w-[34%]">
                <div className="flex items-center gap-1">
                  <BriefcaseBusinessIcon className="h-4 w-4 text-slate-700" />
                  <p className="font-semibold text-slate-700">Role</p>
                </div>
              </TableHead>
              <TableHead className="w-[8%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {orderedCompanyUsers.map((companyUser, index) => {
              const { firstName, lastName, email, isInvited, inviterId } =
                companyUser;
              return (
                <TableRow
                  className={clsx(isInvited && "bg-slate-50")}
                  key={email}
                >
                  <TableCell className="w-[25%]">
                    <p
                      className={clsx(
                        "font-medium",
                        isInvited ? "text-slate-500" : "text-slate-700",
                      )}
                    >
                      {index === 0
                        ? `${firstName} (You)`
                        : `${firstName} ${lastName}`}
                    </p>
                  </TableCell>
                  <TableCell className="w-[33%]">
                    <p
                      className={clsx(
                        "font-medium",
                        isInvited ? "text-slate-500" : "text-slate-700",
                      )}
                    >
                      {email}
                    </p>
                  </TableCell>
                  <TableCell className="w-[34%]">
                    <p
                      className={clsx(
                        "font-medium",
                        isInvited ? "text-slate-500" : "text-slate-700",
                      )}
                    >
                      {isInvited ? "Invited" : "User"}
                    </p>
                  </TableCell>
                  <TableCell className="w-[8%]">
                    {!isInvited || currentUserId !== inviterId ? (
                      <Button size="icon" variant="ghost" disabled>
                        <EllipsisIcon className="h-4 w-4" />
                      </Button>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <EllipsisIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              resendUserInvitation(
                                {
                                  resendUserInvitationRequest: {
                                    inviteeEmail: email,
                                  },
                                },
                                {
                                  onSuccess: () => {
                                    successToast({
                                      description:
                                        "Invite resent successfully!",
                                    });
                                  },
                                },
                              );
                            }}
                          >
                            <RotateCcw className="h-4 w-4" />
                            Resend invite
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              cancelUserInvitation(
                                {
                                  cancelUserInvitationRequest: {
                                    inviteeEmail: email,
                                  },
                                },
                                {
                                  onSuccess: () => {
                                    setRemoveCompanyUserInCompanyUsersForSettings(
                                      {
                                        companyUserToRemove: companyUser,
                                      },
                                    );
                                  },
                                },
                              );
                            }}
                          >
                            <UserRoundX className="h-4 w-4" />
                            Remove from workspace
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
